import React from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation, getLocalizedValue } from "../../utils/translate.js";
import Button from "../comman/button.js";
import { DISH_IMAGE_PLACEHOLDER_HERO } from "../../utils/dishImagePlaceholder.js";

/**
 * DishDetailView component - Shows full dish information
 * @param {Object} props - Component props
 * @param {Object} props.dish - Dish data
 * @returns {JSX.Element} DishDetailView component
 */
const DishDetailView = ({ dish }) => {
    const navigate = useNavigate();
    const { language } = useLanguage();

    if (!dish) {
        return null;
    }

    // Get status badge styles
    const getStatusBadgeStyles = (status) => {
        const colorMap = {
            PENDING: { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' },
            ACTIVE: { bg: '#D1FAE5', text: '#065F46', border: '#34D399' },
            DISABLED: { bg: '#F3F4F6', text: '#6B7280', border: '#D1D5DB' },
            REJECTED: { bg: '#FEE2E2', text: '#991B1B', border: '#F87171' }
        };
        return colorMap[status] || colorMap.DISABLED;
    };

    const statusStyles = getStatusBadgeStyles(dish.status);
    const dishName = getLocalizedValue(dish, 'name', language);
    const dishIngredients = getLocalizedValue(dish, 'ingredient', language);
    const dishPortion = getLocalizedValue(dish, 'portion', language);

    return (
        <div className="max-w-4xl mx-auto">
            {/* Header with status badge */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-[#c9d1d9]">
                    {dishName}
                </h1>
                <span
                    className="inline-block text-xs font-bold px-4 py-2 rounded-full shadow-sm"
                    style={{
                        backgroundColor: statusStyles.bg,
                        color: statusStyles.text,
                        border: `1.5px solid ${statusStyles.border}`
                    }}
                >
                    {getTranslation(`dishStatuses.${dish.status?.toLowerCase()}`, language) || dish.status}
                </span>
            </div>

            {/* Image */}
            <div className="mb-6">
                <div className="w-full h-64 sm:h-80 overflow-hidden rounded-xl bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] relative">
                    <img
                        key={dish.url} // Use URL as key to prevent unnecessary re-renders
                        src={dish.url || DISH_IMAGE_PLACEHOLDER_HERO}
                        alt={dishName}
                        className="w-full h-full object-cover transition-opacity duration-300"
                        loading="eager" // Eager load since detail view is immediately visible
                        decoding="async"
                        fetchpriority="low" // Low priority for detail view images
                        onLoad={(e) => {
                            e.target.style.opacity = "1";
                        }}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = DISH_IMAGE_PLACEHOLDER_HERO;
                            e.target.style.opacity = "1";
                        }}
                        style={{ opacity: 0 }}
                    />
                </div>
            </div>

            {/* Dish Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                    {/* Price */}
                    <div className="bg-white dark:bg-[#161b22] rounded-lg p-4 border border-gray-200 dark:border-[#30363d]">
                        <label className="text-sm font-semibold text-gray-600 dark:text-[#8b949e] mb-2 block">
                            {getTranslation('menu.price', language)}
                        </label>
                        <p className="text-xl font-bold text-gray-900 dark:text-[#c9d1d9]">
                            {dish.price === 0 ? getTranslation('menu.configurable', language) : `${dish.price} AMD`}
                        </p>
                    </div>

                    {/* Dish Type */}
                    {dish.type && (
                        <div className="bg-white dark:bg-[#161b22] rounded-lg p-4 border border-gray-200 dark:border-[#30363d]">
                            <label className="text-sm font-semibold text-gray-600 dark:text-[#8b949e] mb-2 block">
                                {getTranslation('menu.dishType', language)}
                            </label>
                            <p className="text-base font-medium text-gray-900 dark:text-[#c9d1d9]">
                                {getTranslation(`dishTypes.${dish.type}`, language) || dish.type}
                            </p>
                        </div>
                    )}

                    {/* Portion */}
                    {dishPortion && (
                        <div className="bg-white dark:bg-[#161b22] rounded-lg p-4 border border-gray-200 dark:border-[#30363d]">
                            <label className="text-sm font-semibold text-gray-600 dark:text-[#8b949e] mb-2 block">
                                {getTranslation('menu.portion', language)}
                            </label>
                            <p className="text-base font-medium text-gray-900 dark:text-[#c9d1d9]">
                                {dishPortion}
                            </p>
                        </div>
                    )}

                    {/* Expiration Days */}
                    <div className="bg-white dark:bg-[#161b22] rounded-lg p-4 border border-gray-200 dark:border-[#30363d]">
                        <label className="text-sm font-semibold text-gray-600 dark:text-[#8b949e] mb-2 block">
                            {getTranslation('menu.expirationDays', language)}
                        </label>
                        <p className="text-base font-medium text-gray-900 dark:text-[#c9d1d9]">
                            {dish.expirationDate} {getTranslation('common.days', language) || 'days'}
                        </p>
                    </div>

                    {/* Order Before Days */}
                    {dish.orderBefore > 0 && (
                        <div className="bg-white dark:bg-[#161b22] rounded-lg p-4 border border-gray-200 dark:border-[#30363d]">
                            <label className="text-sm font-semibold text-gray-600 dark:text-[#8b949e] mb-2 block">
                                {getTranslation('menu.preOrderDays', language)}
                            </label>
                            <p className="text-base font-medium text-gray-900 dark:text-[#c9d1d9]">
                                {dish.orderBefore} {getTranslation('common.days', language) || 'days'}
                            </p>
                        </div>
                    )}
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                    {/* Ingredients */}
                    {dishIngredients && (
                        <div className="bg-white dark:bg-[#161b22] rounded-lg p-4 border border-gray-200 dark:border-[#30363d]">
                            <label className="text-sm font-semibold text-gray-600 dark:text-[#8b949e] mb-2 block">
                                {getTranslation('menu.ingredients', language)}
                            </label>
                            <p className="text-sm text-gray-900 dark:text-[#c9d1d9] leading-relaxed">
                                {dishIngredients}
                            </p>
                        </div>
                    )}

                    {/* Spice Level */}
                    {dish.spiceLevel && (
                        <div className="bg-white dark:bg-[#161b22] rounded-lg p-4 border border-gray-200 dark:border-[#30363d]">
                            <label className="text-sm font-semibold text-gray-600 dark:text-[#8b949e] mb-2 block">
                                {getTranslation('menu.spiceLevel', language)}
                            </label>
                            <p className="text-base font-medium text-gray-900 dark:text-[#c9d1d9]">
                                {getTranslation(`spiceLevels.${dish.spiceLevel}`, language) || dish.spiceLevel}
                            </p>
                        </div>
                    )}

                    {/* Additions */}
                    {dish.dishAdditionDtoList && dish.dishAdditionDtoList.length > 0 && (
                        <div className="bg-white dark:bg-[#161b22] rounded-lg p-4 border border-gray-200 dark:border-[#30363d]">
                            <label className="text-sm font-semibold text-gray-600 dark:text-[#8b949e] mb-3 block">
                                {getTranslation('menu.additions', language)}
                            </label>
                            <div className="space-y-2">
                                {dish.dishAdditionDtoList.map((addition, index) => (
                                    <div key={index} className="flex justify-between items-center text-sm">
                                        <span className="text-gray-900 dark:text-[#c9d1d9] font-medium">
                                            {getLocalizedValue(addition, 'name', language)}
                                        </span>
                                        <span className="text-gray-600 dark:text-[#8b949e] font-medium">
                                            {addition.overridedPrice !== null && addition.overridedPrice !== undefined 
                                                ? `${addition.overridedPrice} AMD` 
                                                : `${addition.price} AMD`}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Dietary Options */}
                    {dish.dietaryOptionDtoList && dish.dietaryOptionDtoList.length > 0 && (
                        <div className="bg-white dark:bg-[#161b22] rounded-lg p-4 border border-gray-200 dark:border-[#30363d]">
                            <label className="text-sm font-semibold text-gray-600 dark:text-[#8b949e] mb-3 block">
                                {getTranslation('menu.dietaryOptions', language)}
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {dish.dietaryOptionDtoList.map((option, index) => (
                                    <span
                                        key={index}
                                        className="px-3 py-1 bg-white dark:bg-[#0d1117] text-gray-700 dark:text-[#b1bac4] text-xs font-medium rounded-full border border-gray-200 dark:border-[#30363d]"
                                    >
                                        {getLocalizedValue(option, 'name', language)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex gap-4">
                <Button
                    variant="primary"
                    size="lg"
                    onClick={() => navigate(`/menu/edit/${dish.id}`)}
                    className="flex-1"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    {getTranslation('common.edit', language)}
                </Button>
            </div>
        </div>
    );
};

export default DishDetailView;

